import { useState, useEffect, useCallback, useMemo } from 'react'

import moment from 'moment'
import { isEmpty, map, max, uniqueId } from 'lodash'
import { matchPath, useHistory } from 'react-router'

import { automatedPanelStore, synapse } from 'services/state'
import {
  makeGetCta,
  getCtasMap,
  getSafeData,
  getNotificationLabels,
  hasAutoCta,
  buildNewNotification
} from './helpers'
import useStoreSelector from 'utils/hooks/useStoreSelector'
import { openModal } from 'components/Modal/actions'
import { EXPORT, mapAutomated } from './constants'
import { createHashHistory } from 'history'

const useEventNotifications = (initialNotif = [], initialProcesses = {}) => {
  const history = useHistory()

  const [notificationTooltip, setNotificationTooltip] = useState(false)
  // TODO Improve the user state received from backend
  const currentUser = useStoreSelector(
    'user',
    (state) => state?.preferred_username ?? state?.sub
  )

  const automatedPanel = useStoreSelector(
    'automatedPanel',
    (state) => state.notifications
  )

  const simulatedNotification = useStoreSelector(
    'automatedPanel',
    (state) => state.simulatedNotification
  )

  const [notifications, setNotifications] = useState(initialNotif)
  const [processes, setProcesses] = useState(initialProcesses)

  const isCurrentUser = useCallback(
    (userName) => userName === currentUser,
    [currentUser]
  )

  const clearNotificationTooltip = () => {
    setTimeout(() => {
      setNotificationTooltip(false)
    }, 2000)
  }

  const getCta = useMemo(
    () => makeGetCta({ ctas: getCtasMap({ history, openModal }) }),
    [history, openModal]
  )

  const readNotification = useCallback((id) => {
    setNotifications((notifications) => {
      const newNotifications = Object.assign([], notifications)
      const index = newNotifications.findIndex((item) => item.id === id)
      newNotifications[index] = { ...newNotifications[index], read: true }
      return newNotifications
    })
  }, [])

  const handleNotifications = useCallback(
    (data, automatedPanel) => {
      const safeData = getSafeData(data)
      const mainHist = createHashHistory()
      const { pathname } = mainHist.location
      const routeMatch = matchPath(pathname, {
        path: '/instances/:i/workingtables/:w/:t/documents',
        exact: true
      })

      const id = uniqueId('audit-')
      const { timestamp, userName, action = {}, type } = safeData

      const { params } = action
      const { request = {} } = params

      const { title, message, tParams } = getNotificationLabels(safeData, false)

      if (!isCurrentUser(userName) && type === EXPORT) return

      const cta = getCta(safeData, routeMatch?.params?.w)
      const newNotification = {
        id,
        title,
        message,
        tParams,
        timestamp,
        read: false,
        scenarioName: action?.params?.scenarioName,
        avatarText: userName,
        type: 'notification',
        readAction: () => readNotification(id),
        cta
      }
      const notificationMatch = automatedPanel?.find((notif) => {
        if (!isEmpty(request)) {
          const { requestFe } = notif
          const matches = []
          const beRequestKeys = Object.keys(request)
          const feRequestKeys = Object.keys(requestFe)
          // it is necessary to verify that the request has the same number of keys
          if (beRequestKeys.length === feRequestKeys.length) {
            // It is necessary to verify that the same keys correspond to the same values between the two objects fe and be
            beRequestKeys.forEach((key) => {
              // pushing match result
              const isSameValue =
                request[key]?.toString() === requestFe[key]?.toString()

              matches.push(isSameValue)
            })
            return matches.every((b) => b)
          } else return false
        } else return false
      })

      const apiCallStartTime = !isEmpty(notificationMatch)
        ? notificationMatch.startTime
        : map(max(automatedPanel, 'startTime'))

      const notificationTime = new Date().getTime()
      const timeDiff = notificationTime - apiCallStartTime

      setNotifications((prev) => [newNotification, ...prev])

      setNotificationTooltip(true)
      clearNotificationTooltip()

      if (!isEmpty(notificationMatch)) {
        // Deleting the notification from the automatedPanelStore once it has been matched
        automatedPanelStore.update((prev) => ({
          ...prev,
          notifications: prev.notifications.filter(
            ({ startTime }) => startTime !== apiCallStartTime
          )
        }))
      }

      if (
        isCurrentUser(userName) &&
        hasAutoCta(timeDiff, type, mapAutomated) &&
        cta &&
        !isEmpty(notificationMatch)
      ) {
        cta()
      }
    },
    [getCta, isCurrentUser, automatedPanel]
  )

  const handleProcesses = useCallback(
    (data) => {
      if (currentUser !== data.userName) return

      setProcesses((prev) => {
        const { progress, userName, type, id } = data
        const { title, message } = getNotificationLabels(data, true)
        const idNotification = id || uniqueId('toolkit-')

        const newProcess = {
          progress,
          userName,
          id: idNotification,
          title,
          type,
          avatarText: type || '',
          message,
          cta: getCta(data),
          timestamp: prev[title]?.timestamp ?? moment().local().valueOf()
        }

        return { ...prev, [id || title]: newProcess }
      })
    },
    [getCta]
  )

  useEffect(() => {
    if (!isEmpty(simulatedNotification)) {
      const foundNotif = automatedPanel.find(
        ({ id }) => id === simulatedNotification.id
      )

      if (foundNotif) {
        const notification = buildNewNotification(
          foundNotif,
          currentUser,
          simulatedNotification
        )

        handleNotifications(notification, automatedPanel)
      }

      // Deleting the notification from the automatedPanelStore once it has been matched
      automatedPanelStore.update((prev) => ({
        ...prev,
        simulatedNotification: {},
        notifications: prev.notifications.filter(
          ({ id }) => id !== simulatedNotification.id
        )
      }))
    }
  }, [simulatedNotification, automatedPanel, currentUser])

  useEffect(() => {
    const unsubscribes = [
      synapse.subscribe('audit', (e) => {
        const data = JSON.parse(`${e.data}`)
        handleNotifications(data, automatedPanel)
      }),
      synapse.subscribe('toolkit', (e) => {
        const data = JSON.parse(e.data)
        handleProcesses(data)
      })
    ]

    return () => unsubscribes.forEach((fn) => fn())
  }, [automatedPanel])

  return { notifications, processes, setNotifications, notificationTooltip }
}

export default useEventNotifications
